import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import * as crypto from "@/lib/crypto";
import { ensureIdentity } from "@/lib/e2ee";
import { onTrustedDeviceRequest, type TrustedDeviceRequestEvent, type LoginChallengeEvent, type SyncRequestEvent } from "./realtime";
import { useAuth } from "./auth-context";

type Phase = "idle" | "responding" | "done";

/**
 * Mounted once, high in the tree (alongside RealtimeProvider) - an approval
 * request can arrive while the person is anywhere in the app, same reasoning
 * as CallProvider for call signaling. Unifies two backend-distinct event
 * types (login_challenge never transfers identity; sync_request always
 * does) behind one dialog, since both present the same real underlying
 * decision: approve or deny, from a device you're already using.
 */
export function TrustedDeviceApprovalProvider() {
  const { user } = useAuth();
  const [queue, setQueue] = useState<TrustedDeviceRequestEvent[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");

  // Catch up on anything missed while this session wasn't connected - same
  // reasoning as the backend's own listPendingLoginChallenges /
  // listPendingSyncRequests existing specifically for this case (a trusted
  // device that was offline when the real-time event fired).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    async function catchUp() {
      try {
        const [loginChallenges, syncRequests] = await Promise.all([
          api.get<{ id: string; country: string | null; user_agent: string | null; expires_at: string }[]>(
            "/api/auth/login-challenges"
          ),
          api.get<{ id: string; device_label: string | null; ephemeral_public_key: string; expires_at: string }[]>(
            "/api/auth/sync-requests"
          )
        ]);
        if (cancelled) return;
        const missed: TrustedDeviceRequestEvent[] = [
          ...loginChallenges.map(
            (c): LoginChallengeEvent => ({
              type: "login_challenge",
              id: c.id,
              country: c.country,
              userAgent: c.user_agent,
              expiresAt: c.expires_at
            })
          ),
          ...syncRequests.map(
            (r): SyncRequestEvent => ({
              type: "sync_request",
              id: r.id,
              deviceLabel: r.device_label,
              ephemeralPublicKey: r.ephemeral_public_key,
              expiresAt: r.expires_at
            })
          )
        ];
        if (missed.length > 0) setQueue((prev) => [...prev, ...missed]);
      } catch {
        // best-effort - a missed catch-up just means an old request stays
        // pending until it naturally expires, not a functional problem
      }
    }
    catchUp();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    return onTrustedDeviceRequest((event) => {
      setQueue((prev) => (prev.some((e) => e.id === event.id) ? prev : [...prev, event]));
    });
  }, []);

  const current = queue[0] ?? null;

  function dismiss() {
    setQueue((prev) => prev.slice(1));
    setPhase("idle");
  }

  async function handleApprove() {
    if (!current) return;
    setPhase("responding");
    try {
      if (current.type === "login_challenge") {
        await api.post(`/api/auth/login-challenge/${current.id}/respond`, { approve: true });
      } else {
        const identity = await ensureIdentity();
        const sealedIdentityPayload = crypto.sealIdentityForNewDevice(identity, current.ephemeralPublicKey);
        await api.post(`/api/auth/sync/${current.id}/respond`, { approve: true, sealedIdentityPayload });
      }
      setPhase("done");
    } catch {
      // The request likely expired between showing this prompt and tapping
      // approve - dismiss rather than get stuck, the requesting side shows
      // its own "expired" state independently either way.
      dismiss();
    }
  }

  async function handleDeny() {
    if (!current) return;
    setPhase("responding");
    try {
      const path = current.type === "login_challenge" ? "login-challenge" : "sync";
      await api.post(`/api/auth/${path}/${current.id}/respond`, { approve: false });
    } catch {
      // best-effort - denying isn't security-critical to confirm succeeded
      // server-side, the request expires on its own regardless
    }
    dismiss();
  }

  if (!current) return null;

  const isLogin = current.type === "login_challenge";

  return (
    <Dialog open onOpenChange={(open) => !open && phase !== "responding" && dismiss()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isLogin ? "New sign-in requires approval" : "Sync request"}</DialogTitle>
        </DialogHeader>

        {phase === "idle" && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <ShieldAlert className="h-10 w-10 text-amber-500" />
            <div>
              {isLogin ? (
                <p className="text-sm text-muted-foreground">
                  A sign-in from {(current as LoginChallengeEvent).country ?? "an unknown location"} is waiting for
                  your approval. Only approve if that's genuinely you.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {(current as SyncRequestEvent).deviceLabel ?? "A device"} wants to sync your conversations.
                  Approving gives it access to your full message history. Only approve if you're the one holding
                  that device right now.
                </p>
              )}
            </div>
            <div className="flex w-full gap-2">
              <Button variant="outline" className="flex-1" onClick={handleDeny}>
                Deny
              </Button>
              <Button className="flex-1" onClick={handleApprove}>
                Approve
              </Button>
            </div>
          </div>
        )}

        {phase === "responding" && (
          <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Working…</p>
          </div>
        )}

        {phase === "done" && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {isLogin ? "That sign-in is now approved." : "That device can now sync."}
            </p>
            <Button variant="outline" onClick={dismiss}>
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
